import { getInitialState, getTransitionActions, getTransitionCondition, getTransitionTarget, isEventlessTransition, isServiceNode, makeArray } from '../util';
const ACTIVE_CLASS = 'fsmActive';
function sanitizeId(name) {
  const replaced = name.replace(/[^a-zA-Z0-9_]/g, '_') || 'state';
  return /^[0-9]/.test(replaced) ? `s_${replaced}` : replaced;
}
function buildIdMap(names) {
  const map = {};
  const used = new Set();
  names.forEach(name => {
    const base = sanitizeId(name);
    let candidate = base;
    let suffix = 1;
    while (used.has(candidate)) {
      candidate = `${base}_${suffix}`;
      suffix += 1;
    }
    used.add(candidate);
    map[name] = candidate;
  });
  return map;
}
function buildLabel(event, transition) {
  const cond = getTransitionCondition(transition);
  const actions = getTransitionActions(transition);
  const head = [event, cond ? `[${cond}]` : ''].filter(Boolean).join(' ');
  if (actions.length === 0) {
    return head;
  }
  const tail = `/ ${actions.join(', ')}`;
  return head ? `${head} ${tail}` : tail;
}
function transitionLine(fromId, event, transition, resolveId) {
  const target = getTransitionTarget(transition);
  const toId = target ? resolveId(target) : fromId;
  const label = buildLabel(event, transition);
  return label ? `  ${fromId} --> ${toId} : ${label}` : `  ${fromId} --> ${toId}`;
}
function buildForwardEdges(definition) {
  const edges = new Map();
  const stateNames = Object.keys(definition.states);
  const stateSet = new Set(stateNames);
  const addEdge = (from, to) => {
    if (!edges.has(from)) edges.set(from, new Set());
    edges.get(from).add(to);
  };
  stateNames.forEach(name => {
    if (!edges.has(name)) edges.set(name, new Set());
    const node = definition.states[name];
    if (isServiceNode(node)) {
      const {
        invoke
      } = node;
      [invoke.onDone, invoke.onError].forEach(t => {
        if (!t) return;
        makeArray(t).forEach(tr => {
          const target = getTransitionTarget(tr) || name;
          if (stateSet.has(target)) addEdge(name, target);
        });
      });
    }
    if (isEventlessTransition(node)) {
      const {
        always
      } = node;
      makeArray(always).forEach(tr => {
        const target = getTransitionTarget(tr) || name;
        if (stateSet.has(target)) addEdge(name, target);
      });
    }
    const {
      on
    } = node;
    if (on) {
      Object.values(on).forEach(transition => {
        if (!transition) return;
        makeArray(transition).forEach(tr => {
          const target = getTransitionTarget(tr) || name;
          if (stateSet.has(target)) addEdge(name, target);
        });
      });
    }
  });
  if (definition.on) {
    Object.entries(definition.on).forEach(([event, transition]) => {
      if (!transition) return;
      makeArray(transition).forEach(tr => {
        const target = getTransitionTarget(tr);
        if (target && stateSet.has(target)) {
          stateNames.forEach(name => {
            var _on;
            const handledLocally = Boolean((_on = definition.states[name].on) === null || _on === void 0 ? void 0 : _on[event]);
            if (!handledLocally) addEdge(name, target);
          });
        }
      });
    });
  }
  return edges;
}
function getNeighborhood(definition, activeState, radius) {
  var _backward$get;
  const forward = buildForwardEdges(definition);
  const backward = new Map();
  forward.forEach((targets, from) => {
    targets.forEach(to => {
      if (!backward.has(to)) backward.set(to, new Set());
      backward.get(to).add(from);
    });
  });
  const included = new Set([activeState]);
  let frontier = new Set([activeState]);
  for (let hop = 0; hop < radius; hop++) {
    const next = new Set();
    frontier.forEach(state => {
      var _forward$get;
      ((_forward$get = forward.get(state)) !== null && _forward$get !== void 0 ? _forward$get : new Set()).forEach(neighbor => {
        if (!included.has(neighbor)) {
          included.add(neighbor);
          next.add(neighbor);
        }
      });
    });
    frontier = next;
    if (frontier.size === 0) break;
  }

  // Include direct predecessors of the active state (1 hop back)
  ((_backward$get = backward.get(activeState)) !== null && _backward$get !== void 0 ? _backward$get : new Set()).forEach(prev => {
    included.add(prev);
  });
  return included;
}

/**
 * Walks a MachineDefinition and produces Mermaid `stateDiagram-v2` source so a
 * machine can be rendered as a state chart. Pure and dependency-free.
 */
export function transitionsToMermaid(definition, options = {}) {
  const {
    activeState,
    direction = 'LR',
    focusRadius
  } = options;
  const stateNames = Object.keys(definition.states);
  const idMap = buildIdMap(stateNames);
  const resolveId = name => idMap[name] || sanitizeId(name);
  const includedStates = focusRadius != null && activeState ? getNeighborhood(definition, activeState, focusRadius) : null;
  const visibleNames = includedStates ? stateNames.filter(n => includedStates.has(n)) : stateNames;
  const lines = ['stateDiagram-v2'];
  lines.push(`  direction ${direction}`);
  visibleNames.forEach(name => {
    lines.push(`  state "${name}" as ${resolveId(name)}`);
  });
  const initial = getInitialState(definition);
  if (initial && (!includedStates || includedStates.has(initial))) {
    lines.push(`  [*] --> ${resolveId(initial)}`);
  }
  visibleNames.forEach(name => {
    const node = definition.states[name];
    const fromId = resolveId(name);
    const serviceNode = isServiceNode(node);
    const noteParts = [];
    if (serviceNode) {
      const {
        invoke
      } = node;
      if (typeof invoke.src === 'string') {
        noteParts.push(`invokes ${invoke.src}`);
      }
    }
    if (node.tags && node.tags.length > 0) {
      noteParts.push(`tags: ${node.tags.join(', ')}`);
    }
    if (noteParts.length > 0) {
      lines.push(`  note right of ${fromId}`);
      lines.push(`    ${noteParts.join('; ')}`);
      lines.push(`  end note`);
    }
    const inView = target => !includedStates || !target || includedStates.has(target);
    if (serviceNode) {
      const {
        invoke
      } = node;
      if (invoke.onDone) {
        makeArray(invoke.onDone).forEach(transition => {
          if (inView(getTransitionTarget(transition))) lines.push(transitionLine(fromId, 'onDone', transition, resolveId));
        });
      }
      if (invoke.onError) {
        makeArray(invoke.onError).forEach(transition => {
          if (inView(getTransitionTarget(transition))) lines.push(transitionLine(fromId, 'onError', transition, resolveId));
        });
      }
    }
    if (isEventlessTransition(node)) {
      const {
        always
      } = node;
      makeArray(always).forEach(transition => {
        if (inView(getTransitionTarget(transition))) lines.push(transitionLine(fromId, 'always', transition, resolveId));
      });
    }
    const {
      on
    } = node;
    if (on) {
      Object.keys(on).forEach(event => {
        const transition = on[event];
        if (!transition) {
          return;
        }
        makeArray(transition).forEach(singleTransition => {
          if (inView(getTransitionTarget(singleTransition))) lines.push(transitionLine(fromId, event, singleTransition, resolveId));
        });
      });
    }
  });
  const topLevelOn = definition.on;
  if (topLevelOn) {
    Object.keys(topLevelOn).forEach(event => {
      const transition = topLevelOn[event];
      if (!transition) {
        return;
      }
      const transitions = makeArray(transition);
      visibleNames.forEach(name => {
        var _on2;
        const node = definition.states[name];
        const handledLocally = Boolean((_on2 = node.on) === null || _on2 === void 0 ? void 0 : _on2[event]);
        if (handledLocally) {
          return;
        }
        transitions.forEach(singleTransition => {
          const target = getTransitionTarget(singleTransition);
          if (!includedStates || !target || includedStates.has(target)) lines.push(transitionLine(resolveId(name), event, singleTransition, resolveId));
        });
      });
    });
  }
  if (activeState && idMap[activeState]) {
    lines.push(`  classDef ${ACTIVE_CLASS} fill:#c8e6f9,stroke:#0096dd,stroke-width:3px,color:#00356b`);
    lines.push(`  class ${resolveId(activeState)} ${ACTIVE_CLASS}`);
  }
  return lines.join('\n');
}